import ClassCard from "../SelectedClassesListStep/ClassCard";
import { SeedCluster, SeedItem } from "hooks/LabelPropagation/useGetSeed";
import { ProgressCardProps } from "./ProgressCard";
import VLCollapseCard, {
  CollapseCardItem,
} from "views/uikit/Cards/VLCollapseCard";
import { ReactNode, useMemo } from "react";
import pluralize from "pluralize";
import VLMessage from "views/uikit/VLMessage";
import ProgressCardList from "./ProgressCardList";
import { useLocation, useNavigate, useParams } from "react-router";
import { StatsState } from "hooks/LabelPropagation/useGetStats";
import {
  DATA_PAGE_PARAMETER,
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
  REVIEW_TAB_PARAMETER,
  SEED_PARAMETER,
  VERTEX_CONTEXT,
  VERTEX_ID,
  VERTEX_SIMILARITY,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { formatNumber } from "helpers/utility/formatters";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import { useExportSelection } from "hooks/useExportSelection";
import VLProgressBar, {
  VLProgressBarProps,
} from "views/uikit/ProgressBars/VLProgressBar";
import styles from "./style.module.scss";
import { useDispatch } from "react-redux";
import {
  setNavigationCluster,
  setSimilarityVertex,
} from "../../../../redux/SingleDataset/actions";
import {
  getFinalReviewCounterItems,
  isFinalReviewOpen,
  useFinalReviewSummary,
  useOpenFinalReview,
} from "hooks/LabelPropagation/useFinalReview";
import Counters from "../Counters";
import FinalReviewStats from "../FinalReviewStats";

export interface ProgressCardsProps {
  mainProgress: VLProgressBarProps;
  items: ProgressCardProps[];
  totalCount?: string;
}

interface ProgressStepProps {
  initialFlowId?: string | null;
  seedId?: string | null;
  stats?: StatsState;
  loadingType?: LoadingType;
  seed?: SeedCluster[];
  pausedForReview: boolean;
  isFinalReview?: boolean;
}

export type LoadingType = "RUN" | "SUBMIT" | "FINISH" | "APPLY";

/** SUBMIT keeps the progress content (with a spinner), every other loading type replaces it with the main loader */
export const showsMainLoader = (
  loadingType?: LoadingType,
): loadingType is Exclude<LoadingType, "SUBMIT"> =>
  !!loadingType && loadingType !== "SUBMIT";

const CARD_KEYS = {
  REVIEW: "review",
  TRAIN_GOAL: "train-worthy-goal",
  SEED: "seed",
  FINAL_REVIEW: "final-review",
  LABELS: "labels",
} as const;

export default function ProgressStep({
  initialFlowId,
  seedId,
  loadingType,
  stats,
  seed,
  pausedForReview,
  isFinalReview = false,
}: ProgressStepProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { datasetId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const isReviewOpen = searchParams.get(REVIEW_PARAMETER) === "true";
  const isFinalReviewPageOpen =
    isFinalReview && isFinalReviewOpen(searchParams);
  const selectedLabelId = searchParams.get(LABEL_CLASS_PARAMETER);

  const { handleClearAll } = useExportSelection();
  const { data: finalReviewSummary } = useFinalReviewSummary(
    datasetId,
    isFinalReview ? initialFlowId : null,
  );
  const openFinalReview = useOpenFinalReview();
  const labeledCount = finalReviewSummary?.labeled_count ?? 0;
  const counterItems = useMemo(
    () => getFinalReviewCounterItems(finalReviewSummary),
    [finalReviewSummary],
  );

  const activeCards: string[] = useMemo(() => {
    if (isFinalReview) {
      return isFinalReviewPageOpen
        ? [CARD_KEYS.LABELS]
        : [CARD_KEYS.FINAL_REVIEW, CARD_KEYS.LABELS];
    }
    let cards: string[] = [];
    if (stats?.review) {
      cards.push(CARD_KEYS.REVIEW);
    }

    return cards;
  }, [stats, isFinalReview, isFinalReviewPageOpen]);

  const seedItem: CollapseCardItem = {
    header: {
      label: "Seed",
      tooltip:
        "The sample images you provided per label. Seeds cannot be changed after Label Propagation starts",
    },
    key: CARD_KEYS.SEED,
    textButton: formatNumber(
      seed?.reduce((total, s) => total + s.items.length, 0) || 0,
    ),
    children: (
      <div className={styles.seedWrapper}>
        {seed?.map((seed: SeedCluster, clusterIdx: number) => (
          <div key={seed.label + clusterIdx} className={styles.seedItem}>
            <ClassCard
              items={seed.items.map((sample: SeedItem) => ({
                id: sample.id,
                imgUri: sample.imgUri,
                originUri: sample.originUri,
              }))}
              value={seed.label}
              classId="null"
              min={5}
              progressValue={seed.count}
              pausedForReview
            />
          </div>
        ))}
      </div>
    ),
  };

  const labelsItem: CollapseCardItem = {
    header: { label: "Labels" },
    key: CARD_KEYS.LABELS,
    textButton: formatNumber(finalReviewSummary?.labels.length || 0),
    noCollapse: true,
    children: finalReviewSummary ? (
      <Counters
        items={counterItems}
        selectedId={isFinalReviewPageOpen ? selectedLabelId : undefined}
        onClick={(labelId) => datasetId && openFinalReview(datasetId, labelId)}
      />
    ) : (
      <div className={styles.progressContentSpinner}>
        <LoadingOutlined spin={false} />
      </div>
    ),
  };

  const finalReviewItems: CollapseCardItem[] = [
    ...(!isFinalReviewPageOpen
      ? [
          {
            header: {
              label: "Final Review",
              tooltip:
                "Check the propagated labels before they are saved to your dataset",
            },
            key: CARD_KEYS.FINAL_REVIEW,
            children: (
              <VLMessage
                onClick={() => {
                  const firstLabel = counterItems[0];
                  if (!datasetId || !firstLabel) return;
                  openFinalReview(datasetId, firstLabel.id);
                }}
                title="Your labels are ready for a final check"
                description={`${formatNumber(labeledCount)} ${pluralize(
                  "image",
                  labeledCount,
                )} ${pluralize("was", labeledCount)} labeled by Label Propagation. Browse every label, fix wrong ones or ignore images. Labels are saved only when you apply.`}
                textButton="Review labels"
              />
            ),
          },
        ]
      : []),
    labelsItem,
    seedItem,
  ];

  const items: CollapseCardItem[] = [
    ...(stats?.review && loadingType !== "SUBMIT"
      ? [
          {
            header: {
              label: "New Batch For Review",
              progress: stats.review.mainProgress,
              tooltip:
                "Images Label Propagation was not sure about. Review all of them, then Submit to run the next cycle. Fewer batches arrive as confidence grows",
            },
            key: CARD_KEYS.REVIEW,
            textButton: stats.review.totalCount,
            children: !isReviewOpen ? (
              <VLMessage
                onClick={() => {
                  const flow = initialFlowId;
                  if (!datasetId || !seedId || !flow) return;
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.set(SEED_PARAMETER, seedId);
                  searchParams.set(FLOW_PARAMETER, flow);
                  searchParams.set(REVIEW_PARAMETER, "true");
                  searchParams.set(REVIEW_TAB_PARAMETER, "For Review");
                  searchParams.delete(DATA_PAGE_PARAMETER);
                  [
                    VERTEX_SIMILARITY,
                    VERTEX_CONTEXT,
                    VERTEX_ID,
                    VERTEX_TYPE,
                  ].forEach((p) => searchParams.delete(p));
                  dispatch(setSimilarityVertex(null));
                  const classWithItemsForReview = stats.review.items.find(
                    (i) => i.itemLength !== i.maxItems,
                  );
                  if (classWithItemsForReview) {
                    searchParams.set(
                      LABEL_CLASS_PARAMETER,
                      classWithItemsForReview.id,
                    );
                  }
                  dispatch(setNavigationCluster(null));
                  handleClearAll();
                  navigate(
                    {
                      pathname: `/dataset/${datasetId}/data`,
                      search: searchParams.toString(),
                    },
                    { replace: false },
                  );
                }}
                title="New images are waiting for your review"
                description={`A new set of images is ready for your review.\nLabel Propagation is paused until this review is completed.`}
                textButton="Review"
              />
            ) : (
              <ProgressCardList
                items={stats.review.items}
                text={"Images for review"}
              />
            ),
          },
        ]
      : []),
    {
      header: {
        label: "Train-Worthy Goal",
        progress: stats?.labels?.mainProgress,
        tooltip:
          "Labeled images per label toward the training threshold set by your Admin. At 100% the dataset can be sent to Model Jobs",
      },
      textButton: stats?.labels?.totalCount,
      key: CARD_KEYS.TRAIN_GOAL,
      children: stats?.labels ? (
        <ProgressCardList items={stats.labels.items} type="TRAIN_GOAL" />
      ) : null,
    },
    seedItem,
  ];

  const mainProgress = stats?.mainProgress;

  const renderProgressContent = () => {
    if (!mainProgress) {
      return (
        <div className={styles.progressContentSpinner}>
          <LoadingOutlined spin={false} />
        </div>
      );
    }

    return (
      <div className={styles.mainProgressContent}>
        {loadingType === "SUBMIT" && <LoadingOutlined spin={false} />}
        {Math.floor((mainProgress.itemLength * 100) / mainProgress.maxItems)}%
      </div>
    );
  };

  const renderLoaderByType: Record<LoadingType, ReactNode | undefined> = {
    RUN: <VLColorfulLoader text={"Running Label Propagation..."} />,
    SUBMIT: undefined,
    FINISH: (
      <VLColorfulLoader
        text={"Applying your feedback - label propagation in progress..."}
      />
    ),
    APPLY: <VLColorfulLoader text={"Applying labels to your dataset..."} />,
  };
  const isReviewSubmitting = loadingType === "SUBMIT";

  if (showsMainLoader(loadingType)) {
    return (
      <div className={styles.mainLoader}>{renderLoaderByType[loadingType]}</div>
    );
  }

  if (isFinalReview) {
    return (
      <div className={styles.wrapper}>
        {isFinalReviewPageOpen &&
          (finalReviewSummary ? (
            <FinalReviewStats summary={finalReviewSummary} />
          ) : (
            <div className={styles.progressContentSpinner}>
              <LoadingOutlined spin={false} />
            </div>
          ))}
        <VLCollapseCard
          items={finalReviewItems}
          defaultOpenKeys={activeCards}
        />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.totalReviewProgress}>
        <div className={styles.title}>
          <Tooltip title="Your progress through the review budget, about 5 to 10% of the dataset. Label Propagation can finish on its own before the budget is used up, once it is confident enough to label every image">
            <span className={styles.titleWithInfo}>
              Total Review Progress
              <InfoCircleOutlined className={styles.infoIcon} />
            </span>
          </Tooltip>
          <Tooltip title="Images you reviewed out of the total review budget">
            <span className={styles.count}>
              Reviewed {mainProgress?.itemLength} / {mainProgress?.maxItems}
            </span>
          </Tooltip>
        </div>
        <div className={styles.mainProgress}>
          <VLProgressBar
            type={"primary"}
            isActive={isReviewSubmitting}
            maxItems={mainProgress?.maxItems || 0}
            itemLength={mainProgress?.itemLength || 0}
          />
          {renderProgressContent()}
        </div>
      </div>

      {
        <VLCollapseCard
          items={items}
          defaultOpenKeys={activeCards}
          onClose={(key) => {
            if (key === "review") {
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete(REVIEW_PARAMETER);
              searchParams.delete(LABEL_CLASS_PARAMETER);
              navigate(
                {
                  pathname: location.pathname,
                  search: searchParams.toString(),
                },
                { replace: true },
              );
            }
          }}
        />
      }
    </div>
  );
}
